import { Component } from 'react'
import { string, func, object, bool } from 'prop-types'
import { appConnect } from '~/store/hooks'
import { compose } from 'redux'

import store from '~/store'
import { Distance } from 'store/units/types'
import { SYSTEMS } from 'store/units/constants'
import { withUnits } from 'store/units/decorators'
import { mostRecentSelectedObjectStateOfClassName } from 'store/selectedObjects/selectors'
import CLASS_NAMES from 'config/objectClassNames'

import DimensionInput from 'components/UIKit/DimensionInput'
import Grid, { GridItem } from 'components/UIKit/Grid'
import Space from 'components/UIKit/Space'
import Switch from 'components/UIKit/Switch'
import Table, { Row, Cell } from 'components/UIKit/Table'
import TextField from 'components/UIKit/TextField'
import Tooltip from 'components/UIKit/Tooltip'

import Container from './styled/Container'
import SwitchContainer from './styled/SwitchContainer'
import Note from './styled/Note'
import { isHorizontal } from '~/components/DrawingCanvas/util/walls'
import { makeAndDistributeDoors } from '~/store/objects/doors'
import { Modal } from '~/ui/Modal'
import { Dialog } from '~/ui/Dialog'
import { Heading } from 'react-aria-components'
import { Button } from '~/ui/Button'

class DistributeDoorModal extends Component {
  state = {
    itemsCount: 1,
    spacing: new Distance({
      value: 144,
      system: SYSTEMS.IMPERIAL,
    }),
    countAuto: false,
    isInverted: false,
  }

  componentDidUpdate() {
    const { itemsCount, spacing } = this.state

    if (!itemsCount && !spacing) {
      this.setState({
        spacing: new Distance({
          value: 1,
          system: spacing.system,
        }),
      })
    }
  }

  handleFocus = event => {
    event.target.select()
  }

  handleGenerateGrid = () => {
    const { isHorizontal, onDistributeDoors, selectedObjectId } = this.props
    const { itemsCount, isInverted, countAuto } = this.state
    const selectedDoor = Object.values(store.getState().objects.present.doors).find(
      door => door.id === selectedObjectId
    )

    // If auto fill is enabled, fill the wall with doors
    const count = countAuto ? 100 : itemsCount

    onDistributeDoors(selectedDoor, count, this.state.spacing.value, isHorizontal, isInverted)
  }

  handleChange = ({ name, value }) => {
    // keep numeric value between 0 and 50
    const intValue = parseInt(value, 10) || 0
    let formattedValue = intValue >= 50 ? 50 : intValue < 0 ? 0 : intValue
    this.setState({ [name]: formattedValue })
  }

  handleToggleAuto = ({ event, name }) => {
    event.preventDefault()
    event.stopPropagation()
    this.setState({
      [name]: !this.state[name],
    })
  }

  getDirectionText() {
    const { isHorizontal } = this.props
    const { isInverted } = this.state

    if (isHorizontal) {
      return isInverted ? 'left' : 'right'
    } else {
      return isInverted ? 'up' : 'down'
    }
  }

  render() {
    const { distanceUnits, selectedObjectId, isHorizontal } = this.props
    const { isInverted } = this.state
    const isDisabled = !selectedObjectId
    const direction = this.getDirectionText()
    const note = isHorizontal
      ? `The distributed objects will generate to the ${direction}`
      : `The distributed objects will generate ${direction}`

    return (
      <Modal isDismissable>
        <Dialog>
          {({ close }) => (
            <>
              <Heading slot="title" className="text-lg pb-3 mb-3 border-b">
                Distribute Door
              </Heading>
              <Container size="base">
                <Space bottom="l">
                  <Table>
                    <Row>
                      <Cell bordered>
                        <h2>Spacing</h2>
                        <p>Distance between items (edge-to-edge)</p>
                      </Cell>
                      <Cell bordered>
                        <h2>Count</h2>
                        <p>Number of additional items to distribute</p>
                      </Cell>
                    </Row>
                    <Row>
                      <Cell bordered>
                        <Grid vCenter>
                          <GridItem size="2of10">
                            <img
                              src={`/spacing-${isHorizontal ? 'horizontal' : 'vertical'}-edge.png`}
                              width={36}
                              height={36}
                              alt="Spacing"
                            />
                          </GridItem>
                          <GridItem size="3of10">
                            <DimensionInput
                              name="spacing"
                              distance={this.state.spacing}
                              units={distanceUnits}
                              onChange={({ distance }) => {
                                this.setState({ spacing: distance })
                              }}
                            />
                          </GridItem>
                        </Grid>
                      </Cell>
                      <Cell bordered>
                        <Grid vCenter>
                          <GridItem size="2of10">
                            <img
                              src={`/count-${isHorizontal ? 'horizontal' : 'vertical'}.png`}
                              width={36}
                              height={36}
                              alt="Count"
                            />
                          </GridItem>
                          <GridItem size="3of10">
                            <TextField
                              name="count"
                              onFocus={this.handleFocus}
                              onClick={this.handleFocus}
                              type={this.state.itemsCount ? 'number' : 'string'}
                              width="60px"
                              inline
                              value={this.state.itemsCount}
                              disabled={this.state.countAuto}
                              onChange={event => {
                                this.handleChange({
                                  name: 'itemsCount',
                                  value: event.target.value,
                                })
                              }}
                            />
                          </GridItem>
                          <GridItem size="2of10">
                            <Tooltip content="Automatically fill the available area">
                              <Button
                                icon="infinity"
                                primary={this.state.countAuto}
                                onClick={event =>
                                  this.handleToggleAuto({
                                    event,
                                    name: 'countAuto',
                                  })
                                }
                              />
                            </Tooltip>
                          </GridItem>
                        </Grid>
                      </Cell>
                    </Row>
                  </Table>
                </Space>
                <Space bottom="base">
                  <SwitchContainer>
                    <Switch
                      onClick={event => {
                        event.stopPropagation()
                        event.preventDefault()
                        this.setState({ isInverted: !isInverted })
                      }}
                      label={'Invert direction'}
                      isChecked={isInverted}
                    />
                  </SwitchContainer>
                </Space>
                <Space bottom="base">
                  <Note size="s">
                    <strong>Note:</strong> {note}
                  </Note>
                </Space>
              </Container>
              <div className="flex gap-3 justify-end border-t pt-6">
                <Button variant="secondary" onClick={close}>
                  Cancel
                </Button>
                <Button
                  isDisabled={isDisabled}
                  onClick={() => {
                    this.handleGenerateGrid()
                    close()
                  }}
                >
                  Add
                </Button>
              </div>
              {isDisabled && (
                <p className="mt-1 text-xs text-red-500 text-right">Please select a door to genreate from.</p>
              )}
            </>
          )}
        </Dialog>
      </Modal>
    )
  }
}

DistributeDoorModal.propTypes = {
  addDoors: func,
  distanceUnits: string,
  onDistributeDoors: func,
  doors: object,
  isHorizontal: bool,
  isInverted: bool,
  selectedObjectId: string,
}

const mapStateToProps = ({ selectedObjects, objects }) => {
  const selectedObject = mostRecentSelectedObjectStateOfClassName(CLASS_NAMES.DOOR, {
    selectedObjects,
  })
  const selectedObjectId = selectedObject && selectedObject.id
  const selectedDoor = Object.values(objects.present.doors).find(door => door.id === selectedObjectId)
  const segment =
    selectedDoor && Object.values(objects.present.segments).find(segment => segment.id === selectedDoor.wallSegmentId)
  const horizontal = segment && isHorizontal(segment)
  return {
    selectedObjectId,
    isHorizontal: horizontal,
    doors: objects.present.doors,
  }
}

const mapDispatchToProps = dispatch => ({
  onDistributeDoors(door, count, distance, horizontal, inverted) {
    dispatch(makeAndDistributeDoors(door, count, distance, horizontal, inverted))
  },
})

export default compose(appConnect(mapStateToProps, mapDispatchToProps), withUnits)(DistributeDoorModal)
